import { useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { backendUrl, loginUrl, signupUrl } from '../lib/apiUrls';
import { auth, db, isFirebaseConfigured } from '../lib/firebase';
import { clearStoredSession, loadStoredSession, saveStoredUser } from '../lib/storage';

const initialLoginStatus = {
  type: 'idle',
  message: 'Enter your email and password to continue.',
};

function normalizeEmail(email) {
  return email.trim().toLowerCase();
}

function parseChildren(kidsNames) {
  return (kidsNames || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name }));
}

function getLocationTrackingPreference(enabled) {
  if (!enabled) {
    return Promise.resolve({ enabled: false });
  }

  if (!navigator.geolocation) {
    return Promise.resolve({
      enabled: true,
      consentedAt: new Date().toISOString(),
      permissionStatus: 'unavailable',
    });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          enabled: true,
          consentedAt: new Date().toISOString(),
          permissionStatus: 'granted',
          lastKnownLocation: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy ?? null,
            capturedAt: new Date().toISOString(),
          },
        });
      },
      () => {
        resolve({
          enabled: true,
          consentedAt: new Date().toISOString(),
          permissionStatus: 'denied',
        });
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  });
}

function toStoredFirebaseUser(firebaseUser) {
  if (!firebaseUser) {
    return null;
  }

  return {
    userId: firebaseUser.uid,
    firebaseUid: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName || '',
    featureFlags: {},
  };
}

function useAuth() {
  const [currentUser, setCurrentUser] = useState(() => loadStoredSession().user);
  const [email, setEmail] = useState(() => loadStoredSession().user?.email || '');
  const [password, setPassword] = useState('');
  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
  const [loginStatus, setLoginStatus] = useState(() => {
    const stored = loadStoredSession();
    if (stored.user?.email) {
      return {
        type: 'success',
        message: `Saved login found for ${stored.user.email}.`,
      };
    }
    return initialLoginStatus;
  });

  useEffect(() => {
    if (!auth) {
      return undefined;
    }

    return onAuthStateChanged(auth, (firebaseUser) => {
      const user = toStoredFirebaseUser(firebaseUser);
      if (user) {
        saveStoredUser(user);
        setCurrentUser(user);
        setEmail(user.email);
        setLoginStatus({ type: 'success', message: `Signed in as ${user.email}.` });
        return;
      }

      clearStoredSession();
      setCurrentUser(null);
    });
  }, []);

  async function handleLoginSubmit(event) {
    event.preventDefault();

    const trimmedEmail = normalizeEmail(email);
    if (!trimmedEmail) {
      setLoginStatus({ type: 'error', message: 'Email is required.' });
      return;
    }

    if (!password.trim()) {
      setLoginStatus({ type: 'error', message: 'Password is required.' });
      return;
    }

    if (!auth) {
      setLoginStatus({ type: 'error', message: 'Firebase is not configured yet.' });
      return;
    }

    setIsSubmittingLogin(true);
    setLoginStatus({ type: 'loading', message: `Signing in ${trimmedEmail}...` });

    try {
      const credentials = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      const user = toStoredFirebaseUser(credentials.user);

      saveStoredUser(user);
      setCurrentUser(user);
      setEmail(user.email);
      setPassword('');
      setLoginStatus({ type: 'success', message: `Signed in as ${user.email}.` });
    } catch (error) {
      setLoginStatus({ type: 'error', message: error.message || 'Could not complete login.' });
    } finally {
      setIsSubmittingLogin(false);
    }
  }

  function handleLogout() {
    clearStoredSession();
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    setLoginStatus({ type: 'idle', message: 'Saved login cleared. Please sign in again.' });
    if (auth) {
      void signOut(auth);
    }
  }

  async function signUpWithEmail({
    email: rawEmail,
    password: newPassword,
    firstName,
    lastName,
    phone,
    kidsNames,
    locationTrackingEnabled,
  }) {
    if (!auth || !db) {
      throw new Error('Firebase is not configured yet.');
    }

    const loginEmail = normalizeEmail(rawEmail);
    const trimmedFirstName = firstName?.trim() || '';
    const trimmedLastName = lastName?.trim() || '';
    const trimmedPhone = phone?.trim() || '';
    const children = parseChildren(kidsNames);
    const locationTracking = await getLocationTrackingPreference(locationTrackingEnabled);
    const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
    const credentials = await createUserWithEmailAndPassword(auth, loginEmail, newPassword);

    try {
      const firebaseIdToken = await credentials.user.getIdToken();
      let response;

      try {
        response = await fetch(signupUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${firebaseIdToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firebaseUid: credentials.user.uid,
            email: loginEmail,
            ...(fullName ? { name: fullName } : {}),
            ...(trimmedFirstName ? { firstName: trimmedFirstName } : {}),
            ...(trimmedLastName ? { lastName: trimmedLastName } : {}),
            ...(trimmedPhone ? { phone: trimmedPhone } : {}),
            ...(children.length > 0 ? { children } : {}),
            locationTracking,
          }),
        });
      } catch {
        throw new Error(`Could not reach backend at ${backendUrl}.`);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Unable to create user record in backend.');
      }

      await setDoc(doc(db, 'users', credentials.user.uid), {
        username: loginEmail,
        loginEmail,
        ...(trimmedFirstName ? { firstName: trimmedFirstName } : {}),
        ...(trimmedLastName ? { lastName: trimmedLastName } : {}),
        ...(trimmedPhone ? { phone: trimmedPhone } : {}),
        ...(children.length > 0 ? { children } : {}),
        locationTracking,
        createdAt: serverTimestamp(),
      });

      const user = toStoredFirebaseUser(credentials.user);
      saveStoredUser(user);
      setCurrentUser(user);
      setEmail(user.email);
      setLoginStatus({ type: 'success', message: `Signed in as ${user.email}.` });
    } catch (error) {
      await deleteUser(credentials.user);
      throw error;
    }
  }

  return {
    currentUser,
    email,
    setEmail,
    password,
    setPassword,
    isSubmittingLogin,
    loginStatus,
    handleLoginSubmit,
    handleLogout,
    signUpWithEmail,
    isFirebaseConfigured,
    backendUrl,
    loginUrl,
    signupUrl,
  };
}

export default useAuth;
